import { Card, CardContent } from '@mlm/ui';
import type { SlotStanding } from '../../lib/api';
import { fmtNumber } from '../../lib/format';

/** A plan is enabled unless the tenant listed it in disabled_plans. */
function planEnabled(disabled: string[] | undefined, code: string): boolean {
  return !(disabled ?? []).includes(code);
}

/**
 * The slot's MLM standing: membership tier always, then only the metrics whose
 * comp plan the tenant actually runs (Rank → stairstep, PPV/GPV → unilevel or
 * stairstep, Daily Pairs → binary). Kept to a quiet two-column grid so it reads
 * as context next to the wallet, not a stat wall.
 */
export function StandingPanel({
  standing,
  disabledPlanCodes,
  showSponsor,
}: {
  standing: SlotStanding;
  disabledPlanCodes?: string[];
  /** Tenant `show_sponsor_on_dashboard` — show the Sponsor row. */
  showSponsor?: boolean;
}) {
  const showRank = planEnabled(disabledPlanCodes, 'stairstep');
  const showPv =
    planEnabled(disabledPlanCodes, 'unilevel') || planEnabled(disabledPlanCodes, 'stairstep');
  const showBinary = planEnabled(disabledPlanCodes, 'binary');

  const rows: { label: string; value: string; hint?: string }[] = [
    { label: 'Membership', value: standing.membership?.name ?? '—' },
  ];
  if (showSponsor && standing.sponsor) {
    rows.push({
      label: 'Sponsor',
      value: standing.sponsor.ownerName ?? standing.sponsor.slotCode,
      hint: standing.sponsor.slotCode,
    });
  }
  if (showRank) {
    rows.push({ label: 'Rank', value: standing.rank?.code ?? 'Unranked' });
  }
  if (showPv) {
    rows.push({ label: 'Personal PV', value: standing.pv ? fmtNumber(standing.pv.personalPv) : '0' });
    rows.push({ label: 'Group PV', value: standing.pv ? fmtNumber(standing.pv.groupPv) : '0' });
  }
  if (showBinary && standing.binary) {
    const b = standing.binary;
    const flushed = Number(b.flushedToday ?? 0);
    // Legacy/extreme-binary surfaces: a "/max" on the daily pairs count, and a
    // progress row toward the next GC pair-drop milestone (the inner guards on
    // maxPairsPerDay / nextGcMilestone hide them when a tier hasn't configured
    // either, so widening the variant check stays inert for extreme tenants
    // without those mechanics).
    const legacy =
      standing.binaryVariant === 'legacy' || standing.binaryVariant === 'extreme';
    rows.push({
      label: 'Pairs today',
      value:
        legacy && b.maxPairsPerDay > 0
          ? `${fmtNumber(b.pairsToday)}/${fmtNumber(b.maxPairsPerDay)}`
          : fmtNumber(b.pairsToday),
      hint: `L ${fmtNumber(b.leftPv)} · R ${fmtNumber(b.rightPv)}${b.capped ? ' · capped' : ''}${flushed > 0 ? ` · ${fmtNumber(b.flushedToday)} flushed (cap)` : ''}`,
    });
    if (legacy && b.nextGcMilestone) {
      const m = b.nextGcMilestone;
      const hasGc = Number(m.gc) > 0;
      // The drop can pay an explicit GC amount, convert the pairing payout to GC
      // (amount is dynamic), or both.
      const hint =
        hasGc && m.pairingToGc
          ? `pairs · +${fmtNumber(m.gc)} GC + pairing as GC every ${fmtNumber(m.n)}`
          : hasGc
            ? `pairs · +${fmtNumber(m.gc)} GC every ${fmtNumber(m.n)}`
            : `pairs · pairing pays as GC every ${fmtNumber(m.n)}`;
      rows.push({
        label: 'Next GC bonus',
        value: `${fmtNumber(m.pairsInto)}/${fmtNumber(m.n)}`,
        hint,
      });
    }
  }

  // Nothing beyond membership to show (all volume plans off) — still worth the
  // tier, so always render.
  return (
    <Card>
      <CardContent className="p-6">
        <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
          Your standing
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
          {rows.map((r) => (
            <div key={r.label} className="min-w-0">
              <dt className="text-caption text-muted-foreground">{r.label}</dt>
              <dd className="mt-0.5 truncate text-h3 tabular-nums" title={r.value}>
                {r.value}
              </dd>
              {r.hint ? (
                <dd className="text-caption tabular-nums text-muted-foreground">{r.hint}</dd>
              ) : null}
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
